import type { MapDisplaySettingsDTO } from '@nora-monorepo/nora-api-client';
import type { NoraCoordinateFormat } from '@nora-monorepo/shared';
import { NoraSupportedCoordinateFormats } from '@nora-monorepo/shared';

/**
 * Represents the display settings for a Nora map
 */
export class MapDisplaySettings {
  showScaleLine: boolean;
  visibleCoordinateFormats: NoraCoordinateFormat[];

  private constructor(showScaleLine: boolean, visibleCoordinateFormats: NoraCoordinateFormat[]) {
    this.visibleCoordinateFormats = visibleCoordinateFormats;
    this.showScaleLine = showScaleLine;
  }

  /**
   * Will construct a MapDisplaySettings type from the given DTO. If the DTO contains unsupported coordinate formats, the default settings will be returned instead.
   * @param dto
   */
  static fromMapSettingsDTOOrDefault(dto: MapDisplaySettingsDTO | undefined): MapDisplaySettings {
    if (!dto || !MapDisplaySettings.areSupportedCoordinateFormats(dto.visibleCoordinateFormats)) {
      return MapDisplaySettings.defaultSettings();
    }
    return new MapDisplaySettings(dto.showScaleLine, dto.visibleCoordinateFormats);
  }

  /**
   * Returns default map display settings
   */
  static defaultSettings(): MapDisplaySettings {
    return new MapDisplaySettings(true, ['UTM']);
  }

  /**
   * Checks whether the provided coordinate format is enabled.
   * @param coordinateFormat
   */
  isCoordinateFormatEnabled(coordinateFormat: NoraCoordinateFormat): boolean {
    return this.visibleCoordinateFormats.includes(coordinateFormat);
  }

  /**
   * Toggles the provided coordinate format. If the format is currently enabled, it will be disabled, and vice versa.
   * @param coordinateFormat
   */
  toggleCoordinateFormat(coordinateFormat: NoraCoordinateFormat): void {
    if (this.isCoordinateFormatEnabled(coordinateFormat)) {
      this.visibleCoordinateFormats = this.visibleCoordinateFormats.filter(format => format !== coordinateFormat);
    }
    else {
      this.visibleCoordinateFormats = [...this.visibleCoordinateFormats, coordinateFormat];
    }
  }

  toggleScaleLineVisibility(): void {
    this.showScaleLine = !this.showScaleLine;
  }

  private static areSupportedCoordinateFormats(coordinateFormats: string[]): coordinateFormats is NoraCoordinateFormat[] {
    return coordinateFormats.every(format => Object.keys(NoraSupportedCoordinateFormats).includes(format));
  }

  toDTO(): MapDisplaySettingsDTO {
    return {
      showScaleLine: this.showScaleLine,
      visibleCoordinateFormats: this.visibleCoordinateFormats as never,
    };
  }
}
